import React, { useState } from 'react';
import { CheckCircle2Icon, SendIcon } from 'lucide-react';
import { company } from '../data/site';
type Field = 'nom' | 'email' | 'sujet' | 'message';
type Values = Record<Field, string>;
type Errors = Partial<Record<Field, string>>;
const initial: Values = {
  nom: '',
  email: '',
  sujet: '',
  message: ''
};
const labels: Record<Field, string> = {
  nom: 'Nom',
  email: 'E-mail',
  sujet: 'Sujet',
  message: 'Message'
};
function validate(values: Values): Errors {
  const errors: Errors = {};
  if (!values.nom.trim()) errors.nom = 'Merci d’indiquer votre nom.';
  if (!values.email.trim()) {
    errors.email = 'Merci d’indiquer votre e-mail.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) {
    errors.email = 'Cet e-mail semble incorrect.';
  }
  if (!values.sujet.trim()) errors.sujet = 'Merci d’indiquer un sujet.';
  if (!values.message.trim()) errors.message = 'Merci de décrire votre besoin.';
  return errors;
}
export function ContactForm() {
  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const update = (field: Field, value: string) => {
    setValues(v => ({
      ...v,
      [field]: value
    }));
    setErrors(e => ({
      ...e,
      [field]: undefined
    }));
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 600);
  };
  if (status === 'sent') {
    const body = encodeURIComponent(`${values.message}\n\n— ${values.nom} (${values.email})`);
    const subject = encodeURIComponent(values.sujet);
    return <div className="rounded-2xl border border-line bg-surface p-6 sm:p-8" role="status" data-id="element-21" data-mp="vthr0tmef37y">
        <CheckCircle2Icon className="h-8 w-8 text-ember-500" aria-hidden="true" data-id="element-22" data-mp="zu1mlsrbpyrz" />
        <h3 className="mt-4 font-display text-xl font-extrabold text-navy-800" data-id="element-23" data-mp="2wylipa9yc0ap">
          Merci {values.nom.split(' ')[0]}, votre demande est prête.
        </h3>
        <p className="mt-3 text-[0.97rem] leading-relaxed text-navy-500" data-id="element-24" data-mp="35w44whzc0ko6">
          Pour une réponse rapide, envoyez-la par e-mail ou appelez directement —
          c'est souvent le plus efficace.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-25" data-mp="2d91cm7l9166d">
          <a href={`${company.emailHref}?subject=${subject}&body=${body}`} className="inline-flex items-center justify-center gap-2 rounded-xl bg-navy-800 px-5 py-3.5 font-display font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700" data-id="element-26" data-mp="297g38w7xba0x">
            Envoyer par e-mail
          </a>
          <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-xl bg-ember-500 px-5 py-3.5 font-display font-bold text-white transition-colors duration-150 ease-out hover:bg-ember-600" data-id="element-27" data-mp="1ugflg0lae3s4">
            {company.phone}
          </a>
        </div>
        <button type="button" onClick={() => {
        setValues(initial);
        setStatus('idle');
      }} className="mt-5 text-sm font-medium text-navy-400 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-navy-700" data-id="element-28" data-mp="9tpouva43i19">
          Écrire une nouvelle demande
        </button>
      </div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="rounded-2xl border border-line bg-white p-6 shadow-card sm:p-8" data-id="element-29" data-mp="spo3qae63jqc">
      <h3 className="font-display text-xl font-extrabold text-navy-800" data-id="element-30" data-mp="jpizg949t1ge">
        Formulaire de contact
      </h3>
      <p className="mt-2 text-sm text-navy-400" data-id="element-31" data-mp="2zgnjx3cekxsk">
        Les champs marqués d'un astérisque (*) sont requis.
      </p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-32" data-mp="34gt3dw8irdzk">
        {(['nom', 'email', 'sujet'] as Field[]).map(field => <div key={field} className={field === 'sujet' ? 'sm:col-span-2' : undefined} data-id="element-33" data-mp="3jqy9jie4u9by">
            <label htmlFor={field} className="block font-display text-sm font-semibold text-navy-700" data-id="element-34" data-mp="3axcs1ral2dyu">
              {labels[field]} *
            </label>
            <input id={field} name={field} type={field === 'email' ? 'email' : 'text'} value={values[field]} onChange={e => update(field, e.target.value)} aria-invalid={Boolean(errors[field])} aria-describedby={errors[field] ? `${field}-error` : undefined} className={['mt-2 w-full rounded-xl border bg-white px-4 py-3 text-[1rem] text-navy-800 transition-colors duration-150 ease-out placeholder:text-navy-300', errors[field] ? 'border-ember-500' : 'border-line hover:border-navy-200'].join(' ')} data-id="element-35" data-mp="6j4vfhr7eh1h" />
            {errors[field] && <p id={`${field}-error`} className="mt-1.5 text-sm font-medium text-ember-600" data-id="element-36" data-mp="285sezf495mtt">
                {errors[field]}
              </p>}
          </div>)}

        <div className="sm:col-span-2" data-id="element-37" data-mp="2hvv77d3juwr3">
          <label htmlFor="message" className="block font-display text-sm font-semibold text-navy-700" data-id="element-38" data-mp="2lwdxh2psnur6">
            Message *
          </label>
          <textarea id="message" name="message" rows={5} value={values.message} onChange={e => update('message', e.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : undefined} className={['mt-2 w-full resize-y rounded-xl border bg-white px-4 py-3 text-[1rem] text-navy-800 transition-colors duration-150 ease-out placeholder:text-navy-300', errors.message ? 'border-ember-500' : 'border-line hover:border-navy-200'].join(' ')} placeholder="Décrivez votre besoin : type d'équipement, commune, délai souhaité…" data-id="element-39" data-mp="2qx1mxjydk3ja" />
          {errors.message && <p id="message-error" className="mt-1.5 text-sm font-medium text-ember-600" data-id="element-40" data-mp="13g4znm89uv08">
              {errors.message}
            </p>}
        </div>
      </div>

      <button type="submit" disabled={status === 'sending'} className="mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-ember-500 px-6 py-4 font-display text-lg font-bold text-white transition-colors duration-150 ease-out hover:bg-ember-600 disabled:opacity-70 sm:w-auto" data-id="element-41" data-mp="1r3d2zym53tzm">
        {status === 'sending' ? 'Envoi…' : <>
            <SendIcon className="h-5 w-5" aria-hidden="true" data-id="element-42" data-mp="2eq8f7k49n344" />
            Envoyer ma demande
          </>}
      </button>
    </form>;
}